import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { api } from "@/lib/api";
import { sq, useTitle } from "@/lib/img";
import { MaskedLines, FadeUp, ClipReveal, ParallaxImage, EASE } from "@/components/Reveal";
const HERO =
  "https://images.squarespace-cdn.com/content/v1/61f082ef6ee9a55e3636ac4a/f3c9fb51-6a9c-4222-b479-e90419d7b515/Hero-MirabelEdits-1-3.jpg";

const STUDIO_TEASER = "/images/principals-fireplace.jpg";

export default function Home() {
  useTitle("Medium Plenty | Architecture + Interiors");
  const [featured, setFeatured] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "6%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [0.78, 0]);

  useEffect(() => {
    api.get("/projects").then(({ data }) => {
      const f = data.filter((p) => p.featured);
      setFeatured((f.length ? f : data).slice(0, 4));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden bg-ink">
        <motion.div style={{ y: bgY }} className="absolute inset-0">
          <motion.img
            src={sq(HERO, 2000)}
            alt="Casa Aspetto, San Francisco — residence by Medium Plenty"
            className="h-[118%] w-full object-cover"
            initial={{ scale: 1.08, opacity: 0.7 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1.6, ease: EASE }}
          />
        </motion.div>
        <div className="absolute inset-0 bg-ink/30" />
        <motion.div style={{ opacity: fade }} className="absolute inset-x-0 bottom-0 px-6 md:px-12 pb-12 md:pb-16">
          <h1 className="font-serif font-medium text-paper text-[8.5vw] md:text-[4.75vw] leading-[1] tracking-tight whitespace-nowrap">
            <MaskedLines lines={["Architecture + Interiors"]} delay={0.45} />
          </h1>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mt-7">
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 1.05 }}
              className="font-serif font-light text-lg md:text-2xl text-paper [text-shadow:0_1px_16px_rgba(0,0,0,0.45)]"
            >
              A boutique California studio crafting spaces worldwide.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.4 }}
              className="flex items-center gap-3 text-paper/70 text-[11px] uppercase tracking-[0.3em]"
            >
              Scroll
              <motion.span animate={{ y: [0, 6, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                <ArrowDown size={14} strokeWidth={1.5} />
              </motion.span>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* SELECTED WORKS */}
      <section data-testid="home-selected-works" className="px-6 md:px-12 pt-24 md:pt-36 pb-24 md:pb-40">
        <FadeUp className="flex items-end justify-between border-b border-line pb-8">
          <h2 className="font-serif font-light text-3xl md:text-5xl tracking-tight">Selected Works</h2>
          <Link to="/projects" data-testid="home-all-projects-link" className="editorial-link hidden md:inline-flex items-center gap-2 text-sm tracking-[0.08em]">
            All Projects <ArrowRight size={15} strokeWidth={1.5} />
          </Link>
        </FadeUp>

        <div className="space-y-20 md:space-y-28 mt-16">
          {featured.map((p, i) => (
            <FadeUp key={p.slug} delay={0.05}>
              <Link
                to={`/projects/${p.slug}`}
                data-testid={`home-project-${p.slug}`}
                className="group grid md:grid-cols-12 gap-8 md:gap-14 items-end"
              >
                <div className="md:col-span-7">
                  <motion.div whileHover="hover">
                    <ClipReveal src={sq(p.hero_image, 1600)} alt={p.name} className="aspect-[4/3] bg-sand" />
                  </motion.div>
                </div>
                <div className="md:col-span-5">
                  <h3 className="font-serif font-light text-2xl md:text-4xl tracking-tight leading-tight">{p.name}</h3>
                  <p className="text-smoke text-sm mt-2">
                    {p.location} — {p.category}
                  </p>
                  <span className="inline-flex items-center gap-2 mt-5 text-sm text-smoke editorial-link group-hover:text-ink transition-colors duration-300">
                    View Project
                    <ArrowRight
                      size={15}
                      strokeWidth={1.5}
                      className="transition-transform duration-500 group-hover:translate-x-1.5"
                    />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>

        <FadeUp className="mt-16 md:hidden">
          <Link to="/projects" data-testid="home-all-projects-link-mobile" className="editorial-link inline-flex items-center gap-2 text-sm">
            All Projects <ArrowRight size={15} strokeWidth={1.5} />
          </Link>
        </FadeUp>
      </section>

      {/* STUDIO TEASER */}
      <section className="px-6 md:px-12 pt-12 md:pt-20 pb-24 md:pb-40">
        <div className="grid md:grid-cols-12 gap-8 md:gap-14 items-end">
          <FadeUp className="md:col-span-7">
            <p className="kicker mb-4">The Studio</p>
            <ClipReveal
              src={STUDIO_TEASER}
              alt="The principals of Medium Plenty beside a sculptural fireplace"
              className="aspect-[4/5] bg-sand"
            />
          </FadeUp>
          <div className="md:col-span-5">
            <h2 className="font-serif font-light text-xl md:text-2xl lg:text-3xl leading-[1.25] tracking-tight">
              <MaskedLines
                inView
                lines={[
                  "We believe exceptional work",
                  "begins with exceptional clients —",
                  "and that great spaces are",
                ]}
                stagger={0.1}
              />
              <MaskedLines inView lines={["built on connection."]} delay={0.35} lineClassName="italic text-clay" />
            </h2>
            <FadeUp delay={0.5} className="mt-10">
              <Link to="/studio" data-testid="home-studio-link" className="editorial-link text-sm tracking-[0.08em] inline-flex items-center gap-2">
                About the Studio <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* IMAGE INTERLUDE */}
      <section className="px-6 md:px-12 pb-24 md:pb-40">
        <div className="grid md:grid-cols-12 gap-8 items-start">
          <ParallaxImage
            src={sq("https://images.squarespace-cdn.com/content/v1/61f082ef6ee9a55e3636ac4a/3d73587a-7f3e-4a91-a883-0a2c179bc677/MirabelEdits-11.jpg", 1400)}
            alt="Interior detail, Casa Aspetto"
            className="md:col-span-5 aspect-[3/4] bg-sand"
          />
          <div className="md:col-span-6 md:col-start-7 md:pt-32">
            <FadeUp>
              <p className="font-serif font-light text-sm md:text-base leading-[1.4] tracking-tight text-ink">
                “I work in whatever medium likes me at the moment.”
              </p>
              <p className="kicker mt-8">— Marc Chagall</p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* SERVICES STRIP */}
      <section className="px-6 md:px-12 pb-28 md:pb-44">
        <FadeUp className="flex items-end justify-between mb-4">
          <p className="kicker">What We Do</p>
        </FadeUp>
        {[
          ["Architecture", "/studio#architecture"],
          ["Interior Design", "/studio#interior-design"],
          ["Styling & Curation", "/studio#styling-curation"],
        ].map(([s, href], i) => (
          <FadeUp key={s} delay={i * 0.08}>
            <Link
              to={href}
              data-testid={`home-service-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="group flex items-center justify-between border-t border-line py-7 md:py-9 last:border-b"
            >
              <span className="font-serif font-light text-2xl md:text-4xl tracking-tight transition-transform duration-700 ease-out group-hover:translate-x-4">
                {s}
              </span>
              <ArrowRight
                size={28}
                strokeWidth={1}
                className="text-smoke transition-all duration-500 group-hover:translate-x-2 group-hover:text-ink"
              />
            </Link>
          </FadeUp>
        ))}
      </section>
    </div>
  );
}
